/*
 * MySchoolRx public landing page.
 *
 * Visual refresh (MySchoolRx-3a). Content for pillars / FAQs / reporting is
 * DB-driven via /api/products/by-route; hero marketing copy and the Founding
 * Partner offer are fixed in code per the approved refresh brief.
 *
 * Preserved behavior: routes, CTA destinations (via /api/landing-cta), pilot
 * config, referral tracking, analytics, sign-in, and sample-report links.
 *
 * Primary accent: #007a9e (blue). Risk indicators keep their semantic colors.
 * The $4,995 Pricing section is intentionally NOT rendered (kept in source as
 * `PricingArchived` so it can be restored later).
 */
import React, { useEffect, useRef, useState } from "react";
import { GuestNameField } from "./GuestName";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  ClipboardCheck,
  ChevronDown,
  ChevronUp,
  Plus,
  Minus,
  Sparkles,
  Users,
  FileText,
  TrendingUp,
  Menu,
  X,
  Microscope,
  Scale,
  Gauge,
  ArrowDown,
  Layers,
  MessageSquare,
  Grid2x2,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ProofLabel, useScrollReveal } from "./publicChrome";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const ROUTE = "/myschoolrx";

// Primary blue accent (brief: #007a9e). Hover a touch darker.
const BLUE = "#007a9e";
const BLUE_HOVER = "#006583";

const trackFaqEvent = (name, payload = {}) => {
  try {
    axios.post(`${API}/api/analytics/events`, { name, payload: { route: ROUTE, ...payload } });
  } catch (e) { /* non-blocking */ }
};

/* ── Sticky executive nav ── */
const NAV_LINKS = [
  { label: "For Schools", to: "/myschoolrx/schools", external: true },
  { label: "For Portfolios", to: "/myschoolrx/portfolios", external: true },
  { label: "How It Works", to: "#how-it-works" },
  { label: "Research & Experts", to: "/myschoolrx/experts", external: true },
  { label: "FAQs", to: "#faqs" },
];

const TopBar = ({ navigate, cta }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const toggleRef = useRef(null);
  const closeRef = useRef(null);
  const ctaSuffix = cta?.destination_suffix || "get-started";
  const goCta = () => navigate(`${ROUTE}/${ctaSuffix}`);
  const closeMenu = () => { setMobileOpen(false); if (toggleRef.current) toggleRef.current.focus(); };
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") closeMenu(); };
    document.addEventListener("keydown", onKey);
    if (closeRef.current) closeRef.current.focus();
    return () => { document.body.style.overflow = prev; document.removeEventListener("keydown", onKey); };
  }, [mobileOpen]);
  const scrollTo = (hash) => (e) => {
    e.preventDefault();
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <>
    <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#e5e7eb]" data-testid="topbar">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 flex items-center justify-between h-[72px] gap-6">
        <a href="/myschoolrx" className="flex items-center shrink-0" data-testid="topbar-logo">
          {/* MySch∞lRx wordmark (authoritative 6.25:1 asset). height:auto preserves ratio; capped on mobile. */}
          <img src="/assets/myschoolrx-wordmark-600.png" alt="MySchoolRx" className="h-7 w-auto max-sm:h-auto max-sm:max-w-[140px]" />
        </a>
        <nav className="hidden lg:flex items-center gap-7" aria-label="Main" data-testid="topbar-nav">
          {NAV_LINKS.map((l) => (
            <a
              key={l.to}
              href={l.to}
              onClick={l.external ? (e) => { e.preventDefault(); navigate(l.to); } : scrollTo(l.to)}
              className="whitespace-nowrap text-[17px] font-semibold text-[#374151] hover:text-[#111827] transition-colors"
              data-testid={`nav-${l.to.replace(/[#/]/g, "")}`}
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3 shrink-0">
          <a
            href="/diagnostic/login"
            className="hidden sm:inline-flex text-[17px] font-semibold text-[#374151] hover:text-[#111827] transition-colors"
            data-testid="topbar-signin"
          >
            Sign in
          </a>
          <span className="hidden sm:block h-5 w-px bg-[#e5e7eb]" />
          {/* Header enrollment CTA hidden below 640px (available in hero + mobile menu). */}
          <Button
            onClick={goCta}
            className="hidden sm:inline-flex h-9 text-white text-[17px] font-semibold px-4"
            style={{ backgroundColor: BLUE }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BLUE_HOVER)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BLUE)}
            data-testid="topbar-cta"
          >
            Become a Founding Partner
          </Button>
          <button
            type="button"
            ref={toggleRef}
            onClick={() => setMobileOpen(true)}
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-md border border-[#e5e7eb] text-[#111827] hover:bg-[#f9fafb]"
            aria-label="Open menu"
            aria-expanded={mobileOpen ? "true" : "false"}
            aria-controls="caporilli-mobile-menu"
            data-testid="topbar-hamburger"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>
    </header>
    {mobileOpen && (
      <div className="lg:hidden fixed inset-0 z-[60]" id="caporilli-mobile-menu" data-testid="mobile-menu" role="dialog" aria-modal="true" aria-label="Menu" onKeyDown={(e) => { if (e.key === "Escape") closeMenu(); }}>
        <div className="absolute inset-0 bg-black/50" onClick={closeMenu} />
        <div className="absolute top-0 right-0 h-full w-[300px] max-w-[88vw] bg-white shadow-2xl flex flex-col overflow-hidden">
          <div className="flex items-center justify-between h-16 px-4 border-b border-[#e5e7eb] shrink-0">
            <img src="/assets/myschoolrx-wordmark-600.png" alt="MySchoolRx" className="h-7 w-auto max-w-[140px]" />
            <button
              type="button"
              ref={closeRef}
              onClick={() => setMobileOpen(false)}
              className="inline-flex items-center justify-center h-10 w-10 rounded-md hover:bg-[#f3f4f6]"
              aria-label="Close menu"
              data-testid="mobile-menu-close"
            >
              <X className="h-5 w-5 text-[#111827]" />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-1" data-testid="mobile-nav">
            {NAV_LINKS.map((l) => (
              <a
                key={l.to}
                href={l.to}
                onClick={(e) => {
                  e.preventDefault();
                  setMobileOpen(false);
                  if (l.external) {
                    navigate(l.to);
                  } else {
                    setTimeout(() => {
                      const el = document.querySelector(l.to);
                      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }, 80);
                  }
                }}
                className="block px-3 py-3 rounded-md text-[19px] font-semibold text-[#111827] hover:bg-[#f9fafb]"
                data-testid={`mobile-nav-${l.to.replace(/[#/]/g, "")}`}
              >
                {l.label}
              </a>
            ))}
            <a
              href="/diagnostic/login"
              className="block px-3 py-3 mt-3 rounded-md text-[18px] font-semibold text-[#374151] border border-[#e5e7eb] text-center"
              data-testid="mobile-nav-signin"
            >
              Sign in
            </a>
            <Button
              onClick={() => { setMobileOpen(false); goCta(); }}
              className="mt-2 w-full h-11 text-white text-[18px] font-semibold"
              style={{ backgroundColor: BLUE }}
              data-testid="mobile-nav-cta"
            >
              Become a Founding Partner
            </Button>
          </nav>
        </div>
      </div>
    )}
    </>
  );
};

/* ── Hero + Sample Board Risk Snapshot ── */
const Hero = ({ p, navigate }) => {
  const scrollToPaths = () => { const el = document.querySelector("#paths"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };
  // C5: guard against the malformed hero_bg_image value (two URLs joined by %20)
  // that fails to load. Fall back to the local asset; never delete the CMS value.
  const rawBg = p?.section_content?.hero_bg_image;
  const bgImageValid = rawBg && !/\s|%20https?:/i.test(rawBg) && (rawBg.match(/https?:/gi) || []).length <= 1;
  const bgImage = bgImageValid ? rawBg : "/hero-leadership.jpg";
  return (
  <section className="relative overflow-hidden bg-white py-14 lg:py-20" data-testid="hero">
    <div
      className="pointer-events-none absolute inset-0 bg-cover"
      style={{
        backgroundImage: `url('${bgImage}')`,
        backgroundPosition: p?.section_content?.hero_bg_position || "center",
        opacity: (() => {
          const o = parseFloat(p?.section_content?.hero_bg_opacity);
          return isNaN(o) ? 0.16 : o > 1 ? o / 100 : o;
        })(),
      }}
      aria-hidden="true"
    />
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white via-white/92 to-white/60 lg:from-white lg:via-white/88 lg:to-white/30" aria-hidden="true" />
    <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
      <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14 items-start">
        {/* Left column — headline + copy + CTAs */}
        <div>
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[15px] font-semibold tracking-[0.12em] uppercase border"
            style={{ color: "#00607d", backgroundColor: "#e6f4f8", borderColor: "#b3dce7" }}>
            <Sparkles className="h-3.5 w-3.5" /> MySchoolRx
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold text-[#111827] leading-[1.05] tracking-tight" data-testid="hero-headline">
            See the warning signs. Know what needs attention first.
          </h1>
          <p className="mt-6 text-[22px] text-[#4b5563] leading-relaxed max-w-xl" data-testid="hero-subheadline">
            School health depends on more than academic results. MySchoolRx brings eight areas of school performance into a structured view, helping leaders recognize risks, focus priorities, and plan their next steps.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row sm:flex-wrap gap-3">
            <Button
              onClick={scrollToPaths}
              size="lg"
              className="h-12 text-[18px] text-white px-6 whitespace-nowrap w-full sm:w-auto"
              style={{ backgroundColor: BLUE }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BLUE_HOVER)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BLUE)}
              data-testid="hero-primary-cta"
            >
              Find your path <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <button
              onClick={() => navigate(`${ROUTE}/sample-report`)}
              className="inline-flex items-center justify-center h-12 px-5 rounded-md border border-[#d1d5db] text-[#111827] text-[18px] font-semibold hover:bg-[#f9fafb] whitespace-nowrap w-full sm:w-auto"
              data-testid="hero-sample-report-cta"
            >
              View sample report
            </button>
          </div>
        </div>

        {/* Right column — Sample Board Risk Snapshot (illustrative; fictional school). */}
        <div className="relative rounded-xl border border-[#e5e7eb] bg-white p-6 shadow-sm" data-testid="board-snapshot">
          {/* G1: lifted above the card so the chips no longer cover the snapshot header. */}
          <ProofLabel icon={Grid2x2} title="8 pillars scored" sub="115 indicators" className="-top-[54px] -left-3" />
          <ProofLabel icon={AlertTriangle} title="Evidence gaps flagged" sub="Counted per pillar" className="-top-[54px] -right-3" />
          <ProofLabel icon={FileText} title="Board-ready summary" sub="Risk-level report" className="-bottom-4 -left-4" />
          <div className="flex items-center justify-between mb-1">
            <div className="text-[15px] uppercase tracking-[0.14em] text-[#6b7280] font-semibold">Sample Board Risk Snapshot</div>
            <span className="text-[14px] uppercase tracking-wider font-bold text-[#a16207] bg-[#fefce8] border border-[#fde047] rounded px-2 py-0.5">Illustrative</span>
          </div>
          <p className="text-[14px] text-[#6b7280] mb-4"><b className="text-[#374151]">Riverbend Community Charter</b> (fictional) · School Assessment · Fall 2026 · <span className="font-semibold text-[#166534]">Final</span></p>
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg border border-[#e5e7eb] bg-[#f9fafb] p-3">
              <div className="text-[14px] uppercase tracking-wider text-[#6b7280] font-semibold">Overall score</div>
              <div className="text-[22px] font-bold text-[#111827] mt-0.5">70.3<span className="text-[14px] text-[#6b7280] font-semibold">/100</span></div>
              <div className="text-[13px] font-bold text-[#92400e]">Caution</div>
            </div>
            <div className="rounded-lg border border-[#e5e7eb] bg-[#f9fafb] p-3">
              <div className="text-[14px] uppercase tracking-wider text-[#6b7280] font-semibold">Highest-risk pillar</div>
              <div className="text-[17px] font-bold text-[#111827] mt-0.5 leading-tight">Compliance</div>
              <div className="text-[13px] font-bold text-[#991b1b]">44.8 · High Risk</div>
            </div>
            <div className="rounded-lg border border-[#e5e7eb] bg-[#f9fafb] p-3">
              <div className="text-[14px] uppercase tracking-wider text-[#6b7280] font-semibold">Evidence missing</div>
              <div className="text-[22px] font-bold text-[#111827] mt-0.5">12</div>
            </div>
          </div>
          <ul className="mt-4 space-y-2" data-testid="pillar-strip">
            {[
              { name: "Instruction", score: 78.4, band: "Caution", color: "#92400e" },
              { name: "Administrative Leadership", score: 66.2, band: "Caution", color: "#92400e" },
              { name: "Fiscal", score: 81.0, band: "Healthy", color: "#166534" },
              { name: "Governance", score: 63.5, band: "Caution", color: "#92400e" },
              { name: "Compliance", score: 44.8, band: "High Risk", color: "#991b1b" },
              { name: "Student Performance", score: 69.1, band: "Caution", color: "#92400e" },
              { name: "School Management", score: 76.3, band: "Caution", color: "#92400e" },
              { name: "Mission and Key Design Elements", score: 82.7, band: "Healthy", color: "#166534" },
            ].map((row, i) => (
              <li key={i} className="flex items-center gap-3" data-testid={`pillar-row-${i}`}>
                <span className="w-[42%] shrink-0 text-[15px] text-[#374151] leading-tight">{row.name}</span>
                <span className="h-2 flex-1 rounded-full bg-[#f1f5f9] overflow-hidden">
                  <span className="block h-full rounded-full" style={{ width: `${row.score}%`, background: row.color }} />
                </span>
                <span className="w-[108px] shrink-0 text-right text-[14px] font-semibold" style={{ color: row.color }}><b>{row.score.toFixed(1)}</b> {row.band}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 rounded-lg bg-[#fef2f2] border border-[#fecaca] px-3 py-2.5 text-[16px] text-[#7f1d1d] leading-snug">
            <span className="font-semibold">Compliance:</span> Authorizer reporting, evidence organization, and corrective action plan status.
          </div>
          <div className="mt-3 text-[14px] text-[#6b7280]">Sample data only — actual results reflect your responses across {p?.section_content?.diagnostic_question_count || 115} indicators.</div>
        </div>
      </div>
    </div>
  </section>
  );
};

/* ── Problem ── */
const ProblemSection = () => {
  const cards = [
    { icon: Layers, title: "Scattered signals", body: "Important information sits across documents, systems, and conversations." },
    { icon: FileText, title: "Competing priorities", body: "Urgent daily demands can make it difficult to focus on underlying weaknesses." },
    { icon: Gauge, title: "A narrowing window to act", body: "When concerns accumulate, leaders may face more difficult decisions and formal corrective action." },
  ];
  return (
    <section id="problem" className="bg-[#f1f3f5] py-16 lg:py-24" data-testid="problem">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <div className="max-w-3xl">
          <h2 className="text-4xl lg:text-5xl font-extrabold text-[#0f172a] leading-[1.08] tracking-tight">The information is there. The priorities are harder to see.</h2>
          <p className="mt-5 text-[18px] text-[#4b5563] leading-relaxed">School leaders manage reports, policies, financial information, and day-to-day concerns across many different places. The challenge is seeing how those signals connect—and which issues deserve attention first.</p>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          {cards.map((c) => (
            <div key={c.title} className="rounded-xl border border-[#e5e7eb] bg-white p-6" data-testid="problem-card">
              <div className="h-11 w-11 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#e6f4f8" }}>
                <c.icon className="h-5 w-5" style={{ color: BLUE }} />
              </div>
              <h3 className="mt-4 text-[19px] font-bold text-[#0f172a]">{c.title}</h3>
              <p className="mt-2 text-[16px] text-[#4b5563] leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ── How it works (Assess / Prioritize / Act) ── */
const HowItWorksNew = () => {
  const stages = [
    { n: 1, title: "Assess", body: "Bring together your team's knowledge and available evidence." },
    { n: 2, title: "Prioritize", body: "See strengths and areas requiring closer attention." },
    { n: 3, title: "Act", body: "Use the findings to guide decisions, support, and improvement planning." },
  ];
  return (
    <section id="how-it-works" className="bg-white py-16 lg:py-24" data-testid="how">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <div className="max-w-3xl">
          <h2 className="text-4xl lg:text-5xl font-extrabold text-[#0f172a] leading-[1.08] tracking-tight">A clearer picture. A more focused response.</h2>
          <p className="mt-5 text-[18px] text-[#4b5563] leading-relaxed">MySchoolRx helps your team assess conditions across eight pillars, review areas of concern, and use a shared report to guide discussion and next steps.</p>
        </div>
        <ol className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {stages.map((s) => (
            <li key={s.n} className="rounded-xl border border-[#e5e7eb] bg-white p-6" data-testid="how-stage">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full text-white text-[18px] font-bold" style={{ backgroundColor: BLUE }}>{s.n}</span>
              <h3 className="mt-4 text-[20px] font-bold text-[#0f172a]">{s.title}</h3>
              <p className="mt-2 text-[16px] text-[#4b5563] leading-relaxed">{s.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

/* ── Audience paths ── */
const PathsSection = ({ navigate }) => {
  const cards = [
    { key: "school", icon: ShieldCheck, title: "I lead or support a school", body: "For school leaders, leadership teams, and governing board members who want a clearer view of their school's strengths, risks, and priorities.", checks: ["Understand your school's overall health.", "Focus leadership and board discussions.", "Plan your next steps with greater clarity."], cta: "Explore for Schools", to: `${ROUTE}/schools` },
    { key: "portfolio", icon: Layers, title: "I oversee multiple schools", body: "For management organizations, authorizers, and other Portfolio teams seeking a consistent view across the schools they support or oversee.", checks: ["Review schools through a common framework.", "Compare school and Portfolio perspectives.", "Focus follow-up and support."], cta: "Explore for Portfolios", to: `${ROUTE}/portfolios` },
  ];
  return (
    <section id="paths" className="bg-[#f1f3f5] py-16 lg:py-24" data-testid="paths">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-4xl lg:text-5xl font-extrabold text-[#0f172a] leading-[1.08] tracking-tight">How do you work with schools?</h2>
          <p className="mt-4 text-[18px] text-[#4b5563]">Explore the experience that fits your responsibilities.</p>
        </div>
        <div className="mt-8 max-w-md mx-auto"><GuestNameField /></div>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-5xl mx-auto">
          {cards.map((c) => (
            <article key={c.key} className="rounded-2xl border border-[#e5e7eb] bg-white p-7 lg:p-8 flex flex-col" data-testid={`path-${c.key}`}>
              <div className="h-11 w-11 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#e6f4f8" }}>
                <c.icon className="h-5 w-5" style={{ color: BLUE }} />
              </div>
              <h3 className="mt-5 text-[24px] font-bold text-[#0f172a]">{c.title}</h3>
              <p className="mt-3 text-[16px] text-[#4b5563] leading-relaxed">{c.body}</p>
              <ul className="mt-5 space-y-2.5 flex-1">
                {c.checks.map((t) => (
                  <li key={t} className="flex items-start gap-3 text-[16px] text-[#374151]">
                    <span className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: BLUE }} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <Button onClick={() => navigate(c.to)} className="mt-7 h-11 text-white text-[16px] font-semibold self-start px-6" style={{ backgroundColor: BLUE }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BLUE_HOVER)} onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BLUE)}
                data-testid={`path-cta-${c.key}`}>
                {c.cta} <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ── Research & Experts + pillar summary ── */
const EXPERTS = [
  { img: "/assets/rizzo.webp", name: "Dr. Ronald Rizzo, Ph.D.", line: "46 years in education; former Director of Charter Schools, Ferris State University; work with NACSA and the U.S. Department of Education." },
  { img: "/assets/dacosta.webp", name: "Dirk DaCosta, BSc., CTO, CAIO", line: "Co-Founder, 589 Labs. Builds the technology platform behind MySchoolRx." },
];
const PILLARS8 = ["Instruction", "Administrative Leadership", "Fiscal", "Governance", "Compliance", "Student Performance", "School Management", "Mission and Key Design Elements"];
const ResearchSection = () => {
  const steps = [
    { title: "Research", copy: "Research identifies the conditions associated with school quality." },
    { title: "Weight", copy: "Greater influence is given to the variables that matter most." },
    { title: "Score", copy: "The weighted results combine into one clear view of school health." },
  ];
  return (
    <section id="research" className="bg-white py-16 lg:py-24" data-testid="research">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          <div>
            <span className="text-[14px] uppercase tracking-[0.18em] font-semibold" style={{ color: "#00698a" }}>The science behind the score</span>
            <h2 className="mt-3 text-4xl lg:text-5xl font-extrabold text-[#0f172a] leading-[1.08] tracking-tight">Some factors matter more than others.</h2>
            <p className="mt-5 text-[18px] text-[#4b5563] leading-relaxed">School health is complex, and not every factor has the same influence. Built on doctoral research into the conditions and practices associated with school quality, the model identifies the variables that matter most and gives them appropriate weight. Not a simple checklist: a weighted view of school health.</p>
            <ol className="mt-7 space-y-5" data-testid="research-steps">
              {steps.map((s, i) => (
                <li key={s.title} className="flex items-start gap-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[15px] font-bold" style={{ backgroundColor: "#e6f4f8", color: BLUE }}>{i + 1}</span>
                  <div><div className="text-[18px] font-bold text-[#0f172a]">{s.title}</div><p className="mt-1 text-[16px] text-[#4b5563] leading-relaxed">{s.copy}</p></div>
                </li>
              ))}
            </ol>
          </div>
          <div>
            <span className="text-[14px] uppercase tracking-[0.18em] font-semibold" style={{ color: "#00698a" }}>Experts</span>
            <div className="mt-4 space-y-4" data-testid="experts">
              {EXPERTS.map((e) => (
                <div key={e.name} className="flex items-start gap-4 rounded-xl border border-[#e5e7eb] bg-white p-5">
                  <img src={e.img} alt={e.name} width="72" height="72" loading="lazy" className="h-[72px] w-[72px] rounded-full object-cover shrink-0 bg-[#f1f5f9]" />
                  <div><h3 className="text-[18px] font-bold text-[#0f172a]">{e.name}</h3><p className="mt-1 text-[15px] text-[#4b5563] leading-relaxed">{e.line}</p></div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-[16px]"><a href={`${ROUTE}/experts`} className="font-semibold hover:underline" style={{ color: BLUE }} data-testid="experts-link">Meet the experts →</a></p>
          </div>
        </div>
        <div className="mt-14" id="measures">
          <span className="text-[14px] uppercase tracking-[0.18em] font-semibold" style={{ color: "#00698a" }}>What the diagnostic measures</span>
          <h3 className="mt-3 text-[26px] lg:text-[30px] font-extrabold text-[#0f172a]">Eight pillars. One school-quality picture.</h3>
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" aria-label="The eight pillars" data-testid="pillar-chips">
            {PILLARS8.map((name, i) => (
              <li key={name} className="flex items-center gap-3 rounded-lg border border-[#e5e7eb] bg-white px-4 py-3">
                <span className="text-[15px] font-bold" style={{ color: BLUE }}>{String(i + 1).padStart(2, "0")}</span>
                <span className="text-[16px] font-semibold text-[#111827] leading-tight">{name}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

/* ── (old HowItWorks removed in Phase 3; replaced by HowItWorksNew Assess/Prioritize/Act) ── */

/* ── FAQ ──
   Objection-handling FAQ. The first six (differentiation → methodology →
   credibility → weighting → trust → relevance) show by default; "More
   questions" progressively discloses the remaining ten. Order is intentional. */
const FAQ_ITEMS = [
  {
    q: "What makes MySchoolRx different from a checklist or survey?",
    a: "A checklist tells you whether individual conditions are present. MySchoolRx goes further by bringing responses together through a research-based weighted model. Because not every factor has the same influence on school health, the Diagnostic helps leaders identify not simply what needs attention, but what matters most.",
  },
  {
    q: "How is the School Health Score calculated?",
    a: "MySchoolRx uses a research-based weighted model developed from doctoral research examining the conditions and practices associated with school quality. Rather than treating every response equally, the model gives greater influence to the variables that matter most across eight interconnected dimensions of school health.",
  },
  {
    q: "What research is behind the Diagnostic?",
    a: "The methodology grew from doctoral research into the factors associated with school quality. That research informed the variables, dimensions and weighting methodology used by the Diagnostic. MySchoolRx turns that research into a practical tool school leaders can use without needing to understand the underlying statistical methodology.",
  },
  {
    q: "Why is a weighted model important?",
    a: "Because not every weakness carries the same risk. A school can perform well in many areas while having a small number of issues with disproportionately greater influence on overall health. Weighting helps leadership distinguish between something worth noting and something that deserves priority attention.",
  },
  {
    q: "How do we know the thresholds and scores are appropriate for our school?",
    a: "The Diagnostic is based on research into the factors associated with school quality, but the score is not intended to replace leadership judgment or local context. MySchoolRx provides a structured, research-based lens that helps leaders ask better questions, identify priorities and investigate areas requiring attention.\n\nIt supports decision-making; it doesn't replace it.",
  },
  {
    q: "We already track a lot of this information. Why do we need MySchoolRx?",
    a: "That's actually an advantage. MySchoolRx isn't designed to replace the information or systems you already have. It brings critical indicators together into one executive view and helps leadership understand what they mean collectively.\n\nThe objective isn't more data. It's greater clarity from the data and organizational knowledge you already have.",
  },
  {
    q: "Does MySchoolRx replace our SIS, compliance system, authorizer platform or other software?",
    a: "No. Those systems serve important operational purposes. MySchoolRx provides a different layer: a leadership diagnostic designed to help leaders understand overall school health, identify areas of concern and determine where attention should be focused.",
  },
  {
    q: "Is MySchoolRx only for struggling schools?",
    a: "No. MySchoolRx is equally relevant for high-performing and ambitious schools. It can identify strengths worth protecting, emerging vulnerabilities that aren't yet obvious and opportunities for improvement before they become larger problems.\n\nThink of it as a health check, not a distress signal.",
  },
  {
    q: "How much additional work does this create for our staff?",
    a: "The Diagnostic is intentionally structured to minimize administrative burden. Schools complete the assessment using information their leadership teams largely already know or maintain. MySchoolRx then consolidates that information into a structured view that would otherwise require leadership to assemble and interpret across multiple areas.",
  },
  {
    q: "What happens after we receive our score?",
    a: "The score is the beginning, not the end.\n\nThe MySchoolRx helps leadership move from: See → Understand → Act\n\nThe Diagnostic highlights strengths, potential vulnerabilities and areas requiring attention so leadership can establish clearer priorities for improvement.",
  },
  {
    q: "Who should complete the Diagnostic?",
    a: "MySchoolRx is designed to incorporate informed perspectives from people who understand different aspects of the school. Depending on the organization, this may include executive leadership, administrators and other designated participants.\n\nThe workflow allows schools to gather input and review the resulting Diagnostic before finalization.",
  },
  {
    q: "Can boards use the results?",
    a: "Yes. This is one of MySchoolRx's strongest applications.\n\nInstead of overwhelming board members with disconnected operational information, MySchoolRx provides a structured view of school health that helps leadership and boards have better conversations about strengths, risks and priorities.",
  },
  {
    q: "Can organizations with multiple schools use MySchoolRx?",
    a: "Yes. Portfolio, district, authorizer and management users can see individual school results as well as a consolidated view across their schools.\n\nThis makes it easier to identify patterns, recognize strengths and determine where individual schools may need additional attention or support.",
  },
  {
    q: "Does MySchoolRx rank schools against each other?",
    a: "That's not its primary purpose.\n\nThe MySchoolRx is designed to help each school understand its own organizational health and priorities. Portfolio-level views can reveal patterns and differences across schools, but the objective is better decision-making—not creating a simplistic league table.",
  },
  {
    q: "How long does it take to know whether MySchoolRx is valuable?",
    a: "Schools don't need to wait months or years to receive value.\n\nThe initial Diagnostic establishes an immediate baseline of school health and identifies priorities. Repeating the Diagnostic over time adds another valuable dimension by showing whether conditions are improving, remaining stable or moving in the wrong direction.",
  },
  {
    q: "How is our school's information protected?",
    a: "Access to school information is controlled according to authorized users and roles.\n\nThe MySchoolRx focuses on organizational school-health information and does not require individual student records as part of the Diagnostic.",
  },
];
const DEFAULT_FAQ_COUNT = 6;

const FAQItem = ({ item, idx, highlight }) => {
  const [open, setOpen] = useState(false);
  const panelId = `faq-panel-${idx + 1}`;
  const toggle = () => {
    setOpen((o) => {
      if (!o) trackFaqEvent("faq_opened", { question: item.q, index: idx + 1 });
      return !o;
    });
  };
  return (
    <div
      className={`rounded-lg border bg-white transition-shadow hover:shadow-sm ${highlight ? "border-[#e5e7eb] border-l-4 border-l-[#991b1b]" : "border-[#e5e7eb]"}`}
      data-testid={`faq-item-${idx + 1}`}
    >
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={panelId}
        className="w-full flex items-start justify-between gap-4 px-5 py-4 text-left"
      >
        <span className="min-w-0">
          {highlight && (
            <span className="block text-[11px] font-bold uppercase tracking-[0.14em] text-[#991b1b] mb-1">Most asked</span>
          )}
          <span className={`block text-[16px] leading-snug text-[#0f172a] ${highlight ? "font-bold" : "font-semibold"}`}>{item.q}</span>
        </span>
        <span className="shrink-0 mt-0.5 text-[#6b7280]" aria-hidden="true">
          {open ? <Minus className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
        </span>
      </button>
      <div id={panelId} hidden={!open} className="px-5 pb-5 text-[15px] text-[#4b5563] leading-relaxed whitespace-pre-line">
        {item.a}
      </div>
    </div>
  );
};

const FAQ = () => {
  const [expanded, setExpanded] = useState(false);
  const sectionRef = React.useRef(null);
  const visible = expanded ? FAQ_ITEMS : FAQ_ITEMS.slice(0, DEFAULT_FAQ_COUNT);
  const toggle = () => {
    if (expanded) {
      trackFaqEvent("faq_show_less", { total: FAQ_ITEMS.length });
      setExpanded(false);
      requestAnimationFrame(() => sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
    } else {
      trackFaqEvent("faq_more_questions", { revealed: FAQ_ITEMS.length - DEFAULT_FAQ_COUNT });
      setExpanded(true);
    }
  };
  return (
    <section ref={sectionRef} id="faqs" className="bg-[#f1f3f5] py-16 lg:py-24" data-testid="faq">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-[0.75fr_1.6fr] gap-10 lg:gap-14 items-start">
          <div>
            <span className="text-[14px] uppercase tracking-[0.18em] font-semibold" style={{ color: "#00698a" }}>FAQs</span>
            <h2 className="mt-3 text-4xl lg:text-5xl font-extrabold text-[#0f172a] leading-[1.05] tracking-tight">
              Common questions from school leaders
            </h2>
            <p className="mt-4 text-[16px] text-[#4b5563] leading-relaxed">Questions school leaders ask before getting started.</p>
          </div>
          <div>
            <div id="faqs-list" className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
              {visible.map((f, i) => <FAQItem key={i} idx={i} item={f} highlight={i === 0} />)}
            </div>
            <div className="mt-8 flex justify-center lg:justify-start">
              <button
                type="button"
                onClick={toggle}
                aria-expanded={expanded}
                aria-controls="faqs-list"
                className="inline-flex items-center gap-1.5 text-[15px] font-bold uppercase tracking-[0.1em] text-[#991b1b] hover:text-[#7f1d1d] transition-colors"
                data-testid="faq-toggle"
              >
                {expanded ? <>Show less <ChevronUp className="h-4 w-4" /></> : <>More questions <ChevronDown className="h-4 w-4" /></>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── Final Founding Partner CTA ── */
const FinalCTA = ({ navigate, cta }) => (
  <section className="bg-[#f1f3f5] py-16 lg:py-20 border-t border-[#e5e7eb]" data-testid="final-cta">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
      <div>
        <h2 className="text-4xl lg:text-5xl font-extrabold text-[#0f172a] leading-[1.05] tracking-tight">Help shape a clearer approach to school health.</h2>
        <p className="mt-4 text-[18px] text-[#4b5563] leading-relaxed">Join the Founding Partner program, explore the Diagnostic, and share feedback that helps improve the experience.</p>
        <p className="mt-4 text-[16px] text-[#4b5563] font-medium" data-testid="final-offer-line">Free for a limited time. No payment required.</p>
      </div>
      <Button
        onClick={() => navigate(`${ROUTE}/${cta?.destination_suffix || "get-started"}`)}
        size="lg"
        className="h-12 text-[17px] text-white px-8 shrink-0 w-full lg:w-auto"
        style={{ backgroundColor: BLUE }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BLUE_HOVER)}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BLUE)}
        data-testid="final-cta-button"
      >
        Become a Founding Partner
      </Button>
    </div>
  </section>
);

/* ── Footer ── */
const Footer = () => (
  <footer className="bg-white border-t border-[#e5e7eb]" data-testid="footer">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
      <div className="text-[16px] text-[#6b7280]">
        <div data-testid="footer-operator">MySchoolRx is operated by 589 Labs LLC.</div>
        <div className="mt-1">© {new Date().getFullYear()} 589 Labs LLC. All rights reserved.</div>
      </div>
      <nav className="flex items-center gap-5 text-[16px]" aria-label="Footer" data-testid="footer-links">
        <a href="/about" className="text-[#374151] hover:text-[#111827]" data-testid="footer-about">About</a>
        <a href="/contact" className="text-[#374151] hover:text-[#111827]" data-testid="footer-contact">Contact</a>
        <a href="/privacy" className="text-[#374151] hover:text-[#111827]" data-testid="footer-privacy">Privacy</a>
        <a href="/terms" className="text-[#374151] hover:text-[#111827]" data-testid="footer-terms">Terms</a>
        <a href="/careers" className="text-[#374151] hover:text-[#111827]" data-testid="footer-careers-link">Careers</a>
      </nav>
    </div>
  </footer>
);

/* ── Inactive state ── */
const InactiveProduct = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#f9fafb]" data-testid="inactive">
    <div className="max-w-md text-center px-6">
      <ShieldCheck className="h-10 w-10 mx-auto" style={{ color: BLUE }} />
      <h1 className="mt-4 text-[28px] font-bold text-[#111827]">MySchoolRx is currently unavailable.</h1>
      <p className="mt-3 text-[18px] text-[#6b7280]">Please contact 589 Labs for more information.</p>
    </div>
  </div>
);

/* ── Archived $4,995 pricing section — NOT rendered. Kept for easy restore. ──
   To restore: render <PricingArchived p={product} navigate={navigate} /> in the
   page tree below and re-add a "Pricing" nav link + #pricing anchor. */
// eslint-disable-next-line no-unused-vars
const PricingArchived = ({ p, navigate }) => (
  <section id="pricing" className="bg-[#0b1220] py-16 lg:py-20 text-white" data-testid="pricing">
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-10 text-center">
      <h2 className="mt-3 text-3xl lg:text-4xl font-bold leading-tight">Annual license. Predictable cost.</h2>
      <div className="mt-8 inline-flex flex-col items-center rounded-2xl border border-white/20 bg-white/5 backdrop-blur px-10 py-8">
        <div className="mt-2 flex items-baseline gap-1.5" data-testid="price">
          <span className="text-5xl font-bold">${(p?.price ?? 4995).toLocaleString()}</span>
          <span className="text-[20px] text-white/70 font-semibold">/ year</span>
        </div>
        <div className="mt-1 text-[18px] text-white/60">Includes up to 5 users</div>
        <Button onClick={() => navigate(`${ROUTE}/get-started`)} size="lg" className="mt-6 text-white px-6" style={{ backgroundColor: BLUE }}>
          Get Started <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  </section>
);

/* ── PAGE ── */
const CaporilliLanding = () => {
  useScrollReveal();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inactive, setInactive] = useState(false);
  const [ctaCfg, setCtaCfg] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/products/by-route`, { params: { route: ROUTE } });
        setProduct(r.data);
      } catch (e) {
        if (e?.response?.status === 404) setInactive(true);
      } finally {
        setLoading(false);
      }
    })();
    axios.get(`${API}/api/landing-cta`).then((r) => setCtaCfg(r.data)).catch(() => {});
  }, []);

  // Referral attribution.
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) {
      try { sessionStorage.setItem("referral_ref", ref); } catch { /* ignore */ }
      axios.get(`${API}/api/referrals/track`, { params: { ref, event: "visited" } }).catch(() => {});
    }
  }, []);

  // Route-specific metadata (JS-injected; SPA — not present in raw HTML).
  useEffect(() => {
    const title = "MySchoolRx | MySchoolRx";
    const desc = "A research-based school health diagnostic across 8 pillars that turns your school's data into a board-ready risk report. Join as a Founding Partner — free for a limited time.";
    const canonical = "https://www.myschoolrx.com/myschoolrx";
    document.title = title;
    const setMeta = (sel, attr, key, val) => {
      let el = document.head.querySelector(sel);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", val);
    };
    setMeta('meta[name="description"]', "name", "description", desc);
    setMeta('meta[property="og:title"]', "property", "og:title", title);
    setMeta('meta[property="og:description"]', "property", "og:description", desc);
    setMeta('meta[property="og:type"]', "property", "og:type", "website");
    setMeta('meta[property="og:url"]', "property", "og:url", canonical);
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
    link.setAttribute("href", canonical);
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-white text-[#6b7280]">Loading…</div>;
  }
  if (inactive) return <InactiveProduct />;

  return (
    <div className="bg-white" data-testid="landing">
      <TopBar navigate={navigate} cta={ctaCfg} />
      <main id="main" tabIndex={-1} ref={(el) => { if (el && el.id !== "main") el.id = "main"; }}>
        <Hero p={product} navigate={navigate} cta={ctaCfg} />
        <ProblemSection />
        <HowItWorksNew />
        <PathsSection navigate={navigate} />
        <ResearchSection />
        <FAQ />
        <FinalCTA navigate={navigate} cta={ctaCfg} />
      </main>
      <Footer />
    </div>
  );
};

export default CaporilliLanding;
